import { PartyStore } from "../../store/PartyStore.js";
import { InventoryStore } from "../../store/InventoryStore.js";
import { addItem, removeItem } from "../inventory/CharacterInventory.js";
import { DEATH_SAVE_NONE } from "../types/entity.js";
import { pushFeed } from "../Feed.js";
import { EventBus } from "../EventBus.js";
import type { Item } from "../types/items.js";

/**
 * Use one charge of a consumable from `memberId`'s carried stacks.
 *
 * Currently executes `heal` effects: HP is restored up to max, death
 * saves clear on any HP regained (revives a downed member per the
 * deathSaves contract), one unit leaves the stack. Returns false (with
 * no side effects) when the item isn't consumable, isn't carried, or
 * the member is missing.
 */
export function useConsumable(memberId: string, item: Item): boolean {
  const effect = item.consumable;
  if (!effect) return false;
  const member = PartyStore.getMember(memberId);
  if (!member) return false;

  const inv = InventoryStore.get(memberId);
  const result = removeItem(inv, item.id, 1);
  if (result.removed === 0) return false;

  const healed = Math.min(member.maxHp, member.hp + effect.magnitude) - member.hp;
  InventoryStore.set(memberId, result.inventory);
  PartyStore.updateMember(memberId, {
    hp: member.hp + healed,
    ...(healed > 0 ? { deathSaves: DEATH_SAVE_NONE } : {}),
  });
  if (healed > 0) {
    EventBus.emit("combat:healed", { instanceId: memberId, amount: healed });
  }
  pushFeed(
    healed > 0
      ? `${member.name} uses ${item.name} (+${String(healed)} HP).`
      : `${member.name} uses ${item.name} — already at full health.`,
  );
  EventBus.emit("ui:party_update", {});
  return true;
}

export type TransferResult = "ok" | "no_capacity" | "not_found";

/**
 * Move one unit of `item` from one member's carried stacks to another's.
 * Refuses when the receiver's carry capacity would overflow.
 */
export function transferItem(fromId: string, toId: string, item: Item): TransferResult {
  const to = PartyStore.getMember(toId);
  if (!to) return "not_found";

  const fromInv = InventoryStore.get(fromId);
  const removal = removeItem(fromInv, item.id, 1);
  if (removal.removed === 0) return "not_found";

  // addItem enforces the receiver's carry capacity; only commit the
  // removal once the receiving side actually accepted the unit.
  const toInv = InventoryStore.get(toId);
  const addition = addItem(toInv, item, 1, to.abilities.STR);
  if (addition.added === 0) return "no_capacity";

  InventoryStore.set(fromId, removal.inventory);
  InventoryStore.set(toId, addition.inventory);
  EventBus.emit("ui:party_update", {});
  return "ok";
}
